import { jwtDecode } from 'jwt-decode';

// Token storage keys
const ACCESS_TOKEN_KEY = 'token';          // Backward compatible
const REFRESH_TOKEN_KEY = 'refreshToken';
const AUTH_TOKEN_KEY = 'authToken';        // Legacy compatibility

/**
 * Get access token from localStorage
 */
export const getAccessToken = () => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

/**
 * Get refresh token from localStorage
 */
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Store both tokens in localStorage
 */
export const setTokens = (accessToken, refreshToken) => {
  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(AUTH_TOKEN_KEY, accessToken); // Legacy compatibility
  }
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
};

/**
 * Clear all authentication tokens
 */
export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem('user');
};

/**
 * Check if access token is expired or will expire soon
 * @param {number} bufferSeconds - seconds before actual expiry to consider "expired"
 * @returns {boolean} true if token is expired or will expire within buffer
 */
export const isTokenExpired = (bufferSeconds = 60) => {
  const token = getAccessToken();
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    // Check if token expires within bufferSeconds
    return decoded.exp < (currentTime + bufferSeconds);
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;
  }
};

/**
 * Get time until token expires in milliseconds
 * @returns {number} milliseconds until expiry, or 0 if expired/invalid
 */
export const getTokenExpiryTime = () => {
  const token = getAccessToken();
  if (!token) return 0;

  try {
    const decoded = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = (decoded.exp - currentTime) * 1000;
    return Math.max(0, timeUntilExpiry);
  } catch (error) {
    return 0;
  }
};

/**
 * Check if refresh token exists and is potentially valid
 */
export const hasRefreshToken = () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const decoded = jwtDecode(refreshToken);
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp > currentTime;
  } catch (error) {
    // If can't decode, assume it exists but may be invalid
    return true;
  }
};
